import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { Panel, SectionHead, SectionShell } from "./_shared";
import { Coins, ArrowRight } from "lucide-react";

export function RevenueOpportunities({ canReports }) {
  const navigate = useNavigate();
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const ops = useQuery({
    queryKey: ["ops-dashboard"], enabled: canReports,
    queryFn: async () => (await api.get("/ops-dashboard")).data,
  });
  const q = { ...dash, isLoading: dash.isLoading || (canReports && ops.isLoading) };

  return (
    <Panel className="p-5 sm:p-6" data-testid="revenue-opportunities">
      <SectionHead eyebrow="Money Left On The Table" title="Revenue opportunities"
        hint="Grounded in your real pipeline — rupee value shown only where it's authoritative." />
      <SectionShell query={q}>
        {(d) => {
          const cards = ops.data?.cards || {};
          const outstanding = d.revenue?.outstanding || 0;
          // Only outstanding carries an authoritative ₹ value; others are honest COUNTS.
          const rows = [
            { label: "Outstanding payments", amount: outstanding, to: "/invoices", cta: "Collect" },
            { label: "Customers due to return", count: (cards.customers_due_return?.value || 0) + (cards.overdue_returns?.value || 0), to: "/action-center", cta: "Follow up" },
            { label: "Unconverted enquiries", count: d.leads?.new || 0, to: "/leads", cta: "View leads" },
            { label: "Memberships expiring (30d)", count: cards.memberships_expiring?.value || 0, to: "/action-center", cta: "Renew" },
          ].filter((r) => (r.amount || 0) > 0 || (r.count || 0) > 0);

          if (rows.length === 0) {
            return <p className="text-sm text-pawkt-text-secondary" data-testid="opp-empty">No open opportunities detected right now. New signals appear here as your pipeline moves.</p>;
          }
          return (
            <div className="space-y-3">
              <div className="rounded-2xl pawkt-gradient-lavender border border-pawkt-border-soft px-4 py-4">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-pawkt-primary-deep">
                  <Coins className="h-4 w-4" /> Outstanding to collect
                </div>
                <div className="font-display text-3xl font-extrabold text-pawkt-text tabular mt-1">{inr(outstanding)}</div>
              </div>
              <div className="divide-y divide-pawkt-divider">
                {rows.map((r) => (
                  <button key={r.label} data-testid={`opp-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => navigate(r.to)}
                    className="group flex w-full items-center justify-between gap-3 py-3 text-left">
                    <div className="min-w-0">
                      <div className="font-display text-base font-extrabold text-pawkt-text tabular">
                        {r.amount !== undefined ? inr(r.amount) : `${r.count}`}
                        {r.count !== undefined && <span className="ml-1 text-xs font-semibold text-pawkt-text-secondary">{r.count === 1 ? "customer" : "customers"}</span>}
                      </div>
                      <div className="truncate text-xs text-pawkt-text-secondary">{r.label}</div>
                    </div>
                    <span className="flex shrink-0 items-center gap-1 text-xs font-bold text-pawkt-primary-deep group-hover:underline">
                      {r.cta} <ArrowRight className="h-3 w-3" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        }}
      </SectionShell>
    </Panel>
  );
}
